/* FORUM — the build-detail exhibit
 * ===========================================================================
 * Lifted out of Cody's museum handoff unchanged: EXHIBIT_CSS, EXHIBIT_HTML,
 * EXHIBIT_IMG, PRODUCTS and initExhibit() are the designer's, verbatim, with
 * the sixteen embedded photographs replaced by theme-asset URLs. Its own file
 * so the same photography can be reused on the product pages.
 *
 * Exposes window.FORUM_EXHIBIT:
 *   mount({ onLeave, container, mode, product, products })
 *                                 builds the host div and its shadow root,
 *                                 appends it (default document.body) and
 *                                 returns { host, api }. mode:'inline' is
 *                                 the product pages' in-flow mount; product
 *                                 and products pick the opening product and
 *                                 what the switcher offers.
 *   close(mounted)                 the exhibit's own way out, for a caller
 *                                 that has no other handle on it
 *   image(key)                     'assets/hades.webp' -> the asset URL
 *   images                         the whole key -> URL map
 *   PRODUCTS                       the three products' build-point data
 *
 * `api` is initExhibit's own object, renamed nothing: show(key), shut(),
 * isDetailOpen(), key(event), focus(). The museum drives all of it.
 *
 * Generated by handoff/rehearsal/museum/build.mjs — regenerate, don't edit.
 * ===========================================================================
 */
(function () {
  /* The museum publishes its manifest as MUSEUM_ASSETS; the product pages
     publish theirs as EXHIBIT_ASSETS (sections/product-exhibit.liquid). The
     museum's map is still asked first, so that page resolves exactly as it
     did and only a page without one falls through to the new name. */
  const MUSEUM_ASSET = n => (window.MUSEUM_ASSETS || {})[n] ||
                            (window.EXHIBIT_ASSETS || {})[n] || n;
  /* PRODUCTS is declared inside initExhibit, where the designer put it. This
     holds the same object so the product pages can read it too. */
  let EXHIBIT_PRODUCTS = null;

  // ======================================================================
  // Clicking the case opens the FORUM build-detail exhibit. It used to spin
  // the model in place; the exhibit replaces that, so the alcove is now a
  // door rather than a turntable.
  // ======================================================================
  const EXHIBIT_CSS = "\n:host{\n  --void:#0b0b0c;\n  --stage-base:#050506;\n  --stage-glow:#8e8e90;\n  --bone:#f3f1ed;\n  --steel:#9c9ea1;\n  --slate:#6d6f72;\n  --edge:rgba(255,255,255,.13);\n  --edge-soft:rgba(255,255,255,.07);\n  --panel-w:min(400px,33vw);\n  --shift:calc(var(--panel-w) * .46);\n  --ease:cubic-bezier(.22,.61,.31,1);\n  --cond:\"Barlow Semi Condensed\",\"Barlow\",system-ui,sans-serif;\n}\n\n*{box-sizing:border-box}\n::-webkit-scrollbar{width:7px; height:7px}\n::-webkit-scrollbar-track{background:transparent}\n::-webkit-scrollbar-thumb{\n  background:rgba(255,255,255,.16);\n  border:2px solid transparent;\n  background-clip:content-box;\n}\n::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,.34); background-clip:content-box}\n::-webkit-scrollbar-button,::-webkit-scrollbar-corner{display:none}\n/* Was body{}. Inside a shadow root there is no body, so the page-level\n   typography and ground colour move onto the host itself. */\n:host{\n  display:block;\n  background:var(--void);\n  color:var(--bone);\n  font-family:\"Barlow\",system-ui,sans-serif;\n  font-weight:300;\n  -webkit-font-smoothing:antialiased;\n  overflow:auto;\n}\n\n/* \u2500\u2500 frame \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */\n.exhibit{\n  position:relative;\n  /* In the page this sat inside a margin. Here it is the whole overlay, so\n     it fills the host and keeps the inset as padding instead. */\n  min-height:100%;\n  margin:clamp(14px,3vw,40px);\n  overflow:hidden;\n  background:var(--stage-base);\n  isolation:isolate;\n}\n.exhibit::before{ /* the light falling from above */\n  content:\"\";\n  position:absolute; inset:0;\n  background:\n    radial-gradient(115% 78% at 50% -14%,\n      var(--stage-glow) 0%,\n      rgba(112,112,114,.72) 26%,\n      rgba(52,52,54,.55) 52%,\n      rgba(9,9,10,0) 82%);\n  transition:opacity .9s var(--ease);\n  z-index:0;\n}\n.exhibit.is-open::before{opacity:.62}\n\n/* back to the full render \u2014 only surfaces on small screens */\n.back{\n  position:absolute; z-index:8;\n  top:12px; left:14px;\n  display:none; align-items:center; gap:9px;\n  min-height:38px; padding:9px 15px 8px;\n  border:1px solid var(--edge);\n  background:rgba(8,8,9,.62);\n  backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px);\n  color:var(--steel); cursor:pointer;\n  font-family:var(--cond); font-size:10.5px; font-weight:500;\n  letter-spacing:.18em; text-transform:uppercase;\n  opacity:0; pointer-events:none;\n  transition:opacity .45s var(--ease), color .3s, border-color .3s;\n}\n.back .arrow{letter-spacing:0; font-size:13px; line-height:1}\n.back:hover{color:var(--bone); border-color:rgba(255,255,255,.4)}\n.back:focus-visible{outline:1px solid var(--bone); outline-offset:3px}\n\n/* \u2500\u2500 product switcher \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */\n.switch{\n  /* A full-width row with three chips in the middle: the empty space either\n     side was still a click target, and it sits directly over both corner\n     controls. Only the chips take the pointer. */\n  pointer-events:none;\n  position:relative; z-index:9;\n  display:flex; justify-content:center;\n  gap:clamp(16px,2.6vw,40px);\n  padding:clamp(22px,3vh,34px) 20px 0;\n  transition:padding-right 1s var(--ease), opacity .38s var(--ease);\n}\n.is-open .switch{padding-right:calc(20px + var(--panel-w))}\n.switch button{\n  pointer-events:auto;\n  position:relative;\n  padding:0 2px 11px;\n  border:0; background:none; cursor:pointer;\n  font-family:var(--cond); font-size:11px; font-weight:500;\n  letter-spacing:.2em; text-indent:.2em; text-transform:uppercase;\n  color:rgba(243,241,237,.4);\n  transition:color .4s var(--ease);\n}\n.switch button:hover{color:rgba(243,241,237,.82)}\n.switch button[aria-current=\"true\"]{color:var(--bone)}\n.switch button:focus-visible{outline:1px solid var(--bone); outline-offset:4px}\n/* the rule slides to the chosen product rather than blinking across */\n.ink{\n  position:absolute; bottom:0; height:1px;\n  background:var(--bone); pointer-events:none;\n  transition:left .55s var(--ease), width .55s var(--ease);\n}\n\n/* \u2500\u2500 masthead \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */\n.masthead{\n  position:relative; z-index:3;\n  text-align:center;\n  padding:clamp(40px,6vh,76px) 24px 0;\n  transition:padding-right 1s var(--ease);\n}\n.is-open .masthead{padding-right:calc(24px + var(--panel-w))}\n.kicker{\n  margin:1.6em 0 0;\n  font-family:var(--cond);\n  font-size:12px; font-weight:500;\n  letter-spacing:.44em; text-indent:.44em;\n  text-transform:uppercase;\n  color:rgba(243,241,237,.72);\n}\n.wordmark{\n  margin:0;\n  font-family:\"Shippori Mincho B1\",\"Cormorant Garamond\",serif;\n  font-weight:400;\n  font-size:clamp(38px,5.6vw,72px);\n  line-height:.98;\n  text-transform:uppercase;\n  letter-spacing:.17em; text-indent:.17em;\n}\n/* \u2500\u2500 stage \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */\n.upper{position:relative; z-index:2;\n  transition:padding-top .62s var(--ease), opacity .5s var(--ease),\n             transform .55s var(--ease)}\n.rail{transition:opacity .5s var(--ease), transform .55s var(--ease)}\n/* On a swap the whole composition leaves in the direction of travel and\n   the next one arrives from the far side. The exit is quicker than the\n   entrance, so the new product settles rather than snapping into place. */\n.swapping .upper,.swapping .rail{\n  opacity:0;\n  transform:translateX(var(--exit,-30px));\n  transition:opacity .26s var(--ease), transform .28s var(--ease);\n}\n.no-anim .upper,.no-anim .rail{transition:none}\n.stage{\n  position:relative;\n  height:var(--stage-h);\n  perspective:1900px;\n  perspective-origin:50% 42%;\n}\n.rig{\n  position:absolute; left:0; right:0; top:50%;\n  transform:translateY(-50%);\n  transition:transform 1s var(--ease);\n  will-change:transform;\n}\n.is-open .rig{transform:translateY(-50%) translateX(calc(var(--shift) * -1)) scale(.94)}\n\n.hover{\n  position:relative;\n  width:var(--case-w);\n  margin:0 auto;\n  animation:drift 9.5s ease-in-out infinite;\n}\n@keyframes drift{\n  0%,100%{transform:translateY(-9px)}\n  50%{transform:translateY(9px)}\n}\n\n.body{\n  position:relative;\n  transition:transform .5s var(--ease);\n  will-change:transform;\n}\n.body img{\n  display:block; width:100%; height:auto;\n  filter:drop-shadow(0 26px 34px rgba(0,0,0,.62));\n  transition:filter .9s var(--ease);\n  -webkit-user-drag:none; user-select:none;\n}\n.is-open .body img{filter:drop-shadow(0 26px 34px rgba(0,0,0,.62)) brightness(.86) contrast(1.04)}\n\n/* cast shadow, independent of the float */\n.cast{\n  position:absolute; left:9%; right:7%;\n  top:92%; height:64px;\n  background:radial-gradient(50% 50% at 50% 50%, rgba(0,0,0,.82), rgba(0,0,0,0) 71%);\n  filter:blur(19px);\n  animation:settle 9.5s ease-in-out infinite;\n  pointer-events:none;\n}\n@keyframes settle{\n  0%,100%{transform:scale(1.04); opacity:.92}\n  50%{transform:scale(.93); opacity:.68}\n}\n\n/* \u2500\u2500 hotspots \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */\n.point{\n  position:absolute;\n  left:var(--x); top:var(--y);\n  width:42px; height:42px; margin:-21px 0 0 -21px;\n  padding:0; border:0; background:none;\n  cursor:pointer; border-radius:50%;\n  opacity:0; transform:scale(.4);\n  animation:arrive .8s var(--ease) forwards;\n  animation-delay:var(--in);\n  transition:opacity .5s var(--ease);\n}\n@keyframes arrive{to{opacity:1; transform:scale(1)}}\n.point::before{ /* halo ring */\n  content:\"\";\n  position:absolute; inset:9px;\n  border-radius:50%;\n  border:1px solid rgba(255,255,255,.42);\n  box-shadow:0 0 0 1px rgba(0,0,0,.5) inset;\n  transition:inset .42s var(--ease), border-color .42s var(--ease);\n}\n.point::after{ /* core */\n  content:\"\";\n  position:absolute; inset:16px;\n  border-radius:50%;\n  background:var(--bone);\n  box-shadow:0 0 0 1px rgba(0,0,0,.55);\n  transition:inset .42s var(--ease);\n}\n.point:hover::before,.point:focus-visible::before{inset:2px; border-color:rgba(255,255,255,.72)}\n.point:hover::after,.point:focus-visible::after{inset:14px}\n.point:focus-visible{outline:1px solid rgba(255,255,255,.85); outline-offset:6px}\n.point[aria-expanded=\"true\"]::before{inset:0; border-color:rgba(255,255,255,.85)}\n.point[aria-expanded=\"true\"]::after{inset:15px}\n/* while a panel is open the other points drop to a bare dot \u2014 ring gone,\n   core reduced \u2014 so nothing competes with the one being read */\n.is-open .point{opacity:.3}\n.is-open .point::before{inset:16px; border-color:transparent; box-shadow:none}\n.is-open .point::after{inset:18px}\n\n/* the open point, and any the pointer or keyboard is on, stay whole */\n.is-open .point[aria-expanded=\"true\"],\n.is-open .point:hover,\n.is-open .point:focus-visible,\n.is-open .point.is-cued{opacity:1}\n.is-open .point[aria-expanded=\"true\"]::before{inset:0; border-color:rgba(255,255,255,.85); box-shadow:0 0 0 1px rgba(0,0,0,.5) inset}\n.is-open .point[aria-expanded=\"true\"]::after{inset:15px}\n.is-open .point:hover::before,\n.is-open .point:focus-visible::before,\n.is-open .point.is-cued::before{inset:2px; border-color:rgba(255,255,255,.72); box-shadow:0 0 0 1px rgba(0,0,0,.5) inset}\n.is-open .point:hover::after,\n.is-open .point:focus-visible::after,\n.is-open .point.is-cued::after{inset:14px}\n\n/* slow outward pulse, so the markers read as clickable */\n.ping{\n  position:absolute; inset:9px;\n  border-radius:50%;\n  border:1px solid rgba(255,255,255,.6);\n  opacity:0; pointer-events:none;\n  animation:ping 5s cubic-bezier(.16,.7,.3,1) infinite;\n  animation-delay:var(--ping);\n}\n@keyframes ping{\n  0%  {transform:scale(1);   opacity:0}\n  6%  {opacity:.6}\n  40% {transform:scale(2.4); opacity:0}\n  100%{transform:scale(2.4); opacity:0}\n}\n.point:hover .ping,\n.point:focus-visible .ping,\n.point.is-cued .ping,\n.point[aria-expanded=\"true\"] .ping,\n.is-open .ping{animation:none; opacity:0}\n\n/* name that surfaces under the cursor */\n.tip{\n  position:absolute; left:50%; bottom:calc(100% + 6px);\n  transform:translate(var(--tx,-50%),4px);\n  padding:6px 11px 5px;\n  white-space:nowrap;\n  font-family:var(--cond);\n  font-weight:500; font-size:11px;\n  letter-spacing:.15em; text-indent:.15em;\n  text-transform:uppercase;\n  color:var(--bone);\n  background:rgba(9,9,10,.82);\n  border:1px solid var(--edge);\n  backdrop-filter:blur(9px); -webkit-backdrop-filter:blur(9px);\n  opacity:0; pointer-events:none;\n  transition:opacity .26s var(--ease), transform .26s var(--ease);\n}\n.point:hover .tip,\n.point:focus-visible .tip,\n.point[aria-expanded=\"true\"] .tip{opacity:1; transform:translate(var(--tx,-50%),0)}\n\n/* raised from the index rail below */\n.point.is-cued::before{inset:2px; border-color:rgba(255,255,255,.72)}\n.point.is-cued::after{inset:14px}\n.point.is-cued .tip{opacity:1; transform:translate(var(--tx,-50%),0)}\n.is-open .point.is-cued{opacity:1}\n\n/* \u2500\u2500 detail panel \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */\n.detail{\n  position:absolute; z-index:6;\n  top:0; right:0; bottom:0;\n  scroll-margin:20px;\n  padding-block:clamp(28px,4vh,52px);\n  width:var(--panel-w);\n  display:flex; flex-direction:column; justify-content:center;\n  justify-content:safe center;\n  padding:clamp(22px,2.6vh,34px) clamp(28px,2.6vw,42px);\n  background:linear-gradient(200deg, rgba(20,20,21,.9), rgba(6,6,7,.94));\n  backdrop-filter:blur(22px) saturate(.9);\n  -webkit-backdrop-filter:blur(22px) saturate(.9);\n  border-left:1px solid var(--edge);\n  transform:translateX(101%);\n  transition:transform .78s var(--ease);\n  overflow-y:auto;\n  overscroll-behavior:contain;\n  scrollbar-width:thin;\n  scrollbar-color:rgba(255,255,255,.13) transparent;\n}\n.detail::-webkit-scrollbar{width:6px}\n.detail::-webkit-scrollbar-thumb{background:rgba(255,255,255,.13); border:2px solid transparent; background-clip:content-box}\n.detail::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,.3); background-clip:content-box}\n.is-open .detail{transform:translateX(0)}\n\n.shut{\n  align-self:flex-end;\n  flex:0 0 auto;\n  margin:0 0 12px;\n  width:32px; height:32px; padding:0;\n  border:1px solid var(--edge-soft); border-radius:50%;\n  background:none;\n  color:var(--steel);\n  font-size:15px; line-height:1; cursor:pointer;\n  transition:color .3s, border-color .3s;\n}\n.shut:hover{color:var(--bone); border-color:var(--edge)}\n.shut:focus-visible{outline:1px solid var(--bone); outline-offset:3px}\n\n.shot{\n  width:100%;\n  aspect-ratio:4 / 3;\n  margin:0 0 18px;\n  background-color:#08080a;\n  background-repeat:no-repeat;\n  background-size:300% auto;\n  background-position:var(--bgx,50%) var(--bgy,50%);\n  border:1px solid var(--edge-soft);\n  box-shadow:inset 0 0 44px rgba(0,0,0,.7);\n}\n.shot.photo{background-size:cover; background-position:50% 50%}\n.shot.todo{position:relative; outline:1px solid #ff2d87; outline-offset:-1px}\n.shot.todo::after{\n  content:\"Need image\";\n  position:absolute; top:0; left:0;\n  padding:5px 11px 4px;\n  background:#ff2d87; color:#fff;\n  font-family:var(--cond); font-size:10.5px; font-weight:500;\n  letter-spacing:.17em; text-indent:.17em; text-transform:uppercase;\n}\n.detail h3{\n  margin:6px 0 0;\n  font-family:\"Cormorant Garamond\",serif;\n  font-weight:300; font-size:clamp(28px,2.3vw,34px);\n  line-height:1.06; letter-spacing:.012em;\n}\n.detail h3:focus{outline:none}\n.lede{\n  margin:14px 0 0;\n  font-size:14.5px; line-height:1.72;\n  color:rgba(243,241,237,.74);\n  max-width:36ch;\n}\n.step{\n  display:flex; align-items:center; gap:12px;\n  margin-top:16px; padding-top:16px;\n  border-top:1px solid var(--edge-soft);\n}\n.step button{\n  flex:1 1 0;\n  padding:13px 8px 12px;\n  border:1px solid var(--edge);\n  background:none; cursor:pointer;\n  color:var(--steel);\n  font-family:var(--cond); font-size:11px; font-weight:500;\n  letter-spacing:.19em; text-indent:.19em;\n  text-transform:uppercase;\n  transition:color .3s, border-color .3s, background-color .3s;\n}\n.step button:hover{color:var(--bone); border-color:rgba(255,255,255,.4); background-color:rgba(255,255,255,.045)}\n.step button:focus-visible{outline:1px solid var(--bone); outline-offset:3px}\n.tally{flex:0 0 auto; font-family:var(--cond); font-size:11.5px; letter-spacing:.12em; color:var(--slate); font-variant-numeric:tabular-nums}\n\n.fade{animation:fade .5s var(--ease)}\n@keyframes fade{from{opacity:0; transform:translateY(9px)}to{opacity:1; transform:none}}\n\n/* \u2500\u2500 index rail \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */\n.rail{\n  position:relative; z-index:3;\n  display:flex; flex-wrap:wrap; justify-content:center;\n  gap:var(--rail-gap);\n  padding:clamp(22px,3.4vh,34px) 24px clamp(30px,4.4vh,44px);\n  border-top:1px solid var(--edge-soft);\n  margin:0 var(--rail-mx);\n}\n.rail button{\n  padding:0; border:0; background:none; cursor:pointer;\n  font-family:var(--cond); font-size:var(--rail-fs); font-weight:400;\n  letter-spacing:var(--rail-ls); text-transform:uppercase;\n  color:rgba(243,241,237,.42);\n  transition:color .35s var(--ease);\n}\n.rail button:hover{color:rgba(243,241,237,.86)}\n.rail button.is-cued{color:rgba(243,241,237,.86)}\n.rail button[aria-current=\"true\"]{color:var(--bone)}\n.rail button:focus-visible{outline:1px solid var(--bone); outline-offset:5px}\n\n/* \u2500\u2500 narrow \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500 */\n@media (max-width:880px){\n  :host{--panel-w:100%; --shift:0px}\n  /* the title band folds away so the case and its open point own the screen */\n  .masthead{\n    overflow:hidden; max-height:340px;\n    transition:max-height .62s var(--ease), opacity .4s var(--ease),\n               padding .62s var(--ease);\n  }\n  .is-open .masthead{max-height:0; opacity:0; padding-top:0; padding-right:24px}\n  .switch{transition:max-height .5s var(--ease), opacity .35s var(--ease), padding .5s var(--ease);\n          max-height:120px; overflow:hidden}\n  .rail{transition:max-height .5s var(--ease), opacity .35s var(--ease),\n                   border-color .5s var(--ease), transform .55s var(--ease)}\n  .is-open .switch{max-height:0; opacity:0; padding-top:0; padding-right:20px}\n  /* the folded title band leaves a strip for the back control */\n  .is-open .upper{padding-top:56px}\n\n  /* the render and the index fold away entirely, so the open point owns\n     the screen and there is nothing above it to scroll past */\n  .stage{overflow:hidden; transition:height .5s var(--ease), opacity .35s var(--ease)}\n  .is-open .stage{height:0; opacity:0}\n  .rail{\n    overflow:hidden;\n    transition:max-height .5s var(--ease), opacity .35s var(--ease),\n               border-color .5s var(--ease);\n  }\n  .is-open .rail{max-height:0; opacity:0; border-top-color:transparent}\n  .is-open .back{display:flex; opacity:1; pointer-events:auto}\n  .shut{display:none}\n  .stage{height:var(--stage-h-sm)}\n  .hover{width:92vw}\n  .is-open .rig{transform:translateY(-50%) scale(.98)}\n  .detail{\n    position:relative; inset:auto; width:auto;\n    border-left:0; border-top:1px solid var(--edge);\n    transform:none; overflow:hidden;\n    max-height:0; opacity:0;\n    padding:0 22px;\n    transition:max-height .62s var(--ease), opacity .4s var(--ease), padding .62s var(--ease);\n  }\n  .is-open .detail{max-height:820px; opacity:1; padding:30px 22px 28px}\n  /* a moving target is hard to hit with a thumb, so damp the drift right\n     down \u2014 the case still reads as floating, it just holds position */\n  @keyframes drift-touch{\n    0%,100%{transform:translateY(-2.5px)}\n    50%{transform:translateY(2.5px)}\n  }\n  .hover{animation:drift-touch 11s ease-in-out infinite}\n  .cast{animation-duration:11s}\n\n  .exhibit{scroll-margin-top:10px}\n\n  /* eight markers on a 360px-wide case: shrink the footprint so\n     neighbouring points stop overlapping each other */\n  .point{width:34px; height:34px; margin:-17px 0 0 -17px}\n  .point::before,.ping{inset:9px}\n  .point::after{inset:13px}\n  .point:hover::before,.point:focus-visible::before,.point.is-cued::before,.point[aria-expanded=\"true\"]::before{inset:2px}\n  .point:hover::after,.point:focus-visible::after,.point.is-cued::after,.point[aria-expanded=\"true\"]::after{inset:12px}\n  .is-open .point::before{inset:13px}\n  .is-open .point::after{inset:15px}\n  .is-open .point[aria-expanded=\"true\"]::before,\n  .is-open .point:hover::before,\n  .is-open .point:focus-visible::before,\n  .is-open .point.is-cued::before{inset:2px}\n  .is-open .point[aria-expanded=\"true\"]::after,\n  .is-open .point:hover::after,\n  .is-open .point:focus-visible::after,\n  .is-open .point.is-cued::after{inset:12px}\n  .tip{font-size:10px; padding:5px 9px 4px; letter-spacing:.12em; text-indent:.12em}\n  .point.edge-l .tip{left:0; --tx:0}\n  .point.edge-r .tip{left:auto; right:0; --tx:0}\n  /* points high on the lid would push their name off the top of the\n     screen once the page scrolls, so drop those below the marker */\n  .point.edge-t .tip{bottom:auto; top:calc(100% + 6px)}\n\n  .shut{width:44px; height:44px; margin-bottom:10px}\n  .step{gap:10px}\n  .step button{padding:16px 6px 15px; font-size:10.5px; letter-spacing:.14em; text-indent:.14em}\n\n  /* the index carries the interaction on a phone, so give it real rows */\n  .rail{\n    display:grid; grid-template-columns:repeat(var(--rail-cols),1fr);\n    gap:0; padding:0;\n    margin:0 clamp(16px,4vw,28px);\n  }\n  .rail button{\n    min-height:48px;\n    padding:10px 6px;\n    font-size:10px; letter-spacing:.05em; line-height:1.35;\n    border-bottom:1px solid var(--edge-soft);\n  }\n  .rail.cols-2 button:nth-child(odd){border-right:1px solid var(--edge-soft)}\n  .rail button.last-row{border-bottom:0}\n}\n\n@media (prefers-reduced-motion:reduce){\n  .hover,.cast{animation:none}\n  .point{animation:none; opacity:1; transform:none}\n  .ping{animation:none}\n  .fade{animation:none}\n  *{transition-duration:.01ms !important}\n}\n\n\n/* \u2500\u2500 back to the room \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\n   The section's own Back control is the phone-only way out of a build\n   point. This is the way out of the exhibit itself, so it sits opposite\n   and stays up at every width. */\n.leave{\n  position:absolute; z-index:10;\n  top:12px; left:14px;\n  display:inline-flex; align-items:center; gap:9px;\n  min-height:38px; padding:9px 15px 8px;\n  border:1px solid var(--edge);\n  background:rgba(8,8,9,.62);\n  backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px);\n  color:var(--steel); cursor:pointer;\n  font-family:var(--cond); font-size:10.5px; font-weight:500;\n  letter-spacing:.18em; text-transform:uppercase;\n  transition:color .3s, border-color .3s, opacity .45s var(--ease);\n}\n.leave:hover{ color:var(--bone); border-color:rgba(255,255,255,.3) }\n.leave:focus-visible{ outline:1px solid var(--bone); outline-offset:2px }\n/* With a build point open on a phone the panel takes the screen and the\n   section's own Back control appears; two exits at once is one too many. */\n@media (max-width:880px){\n  .exhibit.is-open .leave{ opacity:0; pointer-events:none }\n}\n\n/* The phone keeps the arrow and drops the words: at 360px a full label\n   reaches into the middle of the switcher. */\n.leave .arrow{ font-size:13px; line-height:1 }\n@media (max-width:880px){\n  .leave{ padding:9px 12px 8px }\n  .leave .word{ display:none }\n}\n";

  /* ── inline mode ───────────────────────────────────────────
     Concatenated onto the end of EXHIBIT_CSS when the sheet is built, so
     these land last in the cascade. Every rule sits behind a host attribute
     that only an inline mount sets — the museum's overlay matches none of
     them and reads the designer's stylesheet unchanged.

     The product pages mount the exhibit in the page flow rather than as a
     takeover: no fixed positioning (that lived in the museum's own CSS, on
     #caseExhibit), no inset frame, and a height budget that brings the
     masthead, the render with its points, and the index rail to roughly
     min(100vh, 900px) on a desktop screen. The stage is the part that gives.
     Below 880px the designer's narrow block still rules: the paddings below
     are desktop-only so the phone's fold-away still collapses to nothing,
     and the section simply grows to whatever the open build point needs. */
  const INLINE_CSS = `
:host([data-mode="inline"]){
  position:relative;
  height:auto;
  overflow:visible;            /* the page scrolls, not the exhibit */
}
:host([data-mode="inline"]) .exhibit{
  min-height:0;
  margin:0;                    /* a full-bleed band between two sections */
}
/* V2.72 (Josh, 2026-09-21): with the film band gone the exhibit sits right
   under the feature tabs, and the light falling from above drew a grey edge
   across the page where the two meet. Inline, the exhibit stands on the
   page's own flat obsidian, unlit; the cast shadow still grounds the case.
   The museum overlay matches none of this and keeps the lit stage. */
:host([data-mode="inline"]),
:host([data-mode="inline"]) .exhibit{ background:var(--color-obsidian,#111112) }
:host([data-mode="inline"]) .exhibit::before{ display:none }
/* The build-detail panel was drawn for the lit stage: a dark gradient
   (#141415 to #060607) with a bright rule, which read as glass over the
   light. On the flat ground it read as a darker slab. Inline it takes the
   page's obsidian too, still frosted for the moment it slides over the case,
   and its rule drops to the same hairline the index rail wears. */
:host([data-mode="inline"]) .detail{
  background:rgba(17,17,18,.94);
  background:color-mix(in srgb, var(--color-obsidian,#111112) 94%, transparent);
  border-left-color:var(--edge-soft);
  /* on a phone the panel sits under the folded masthead and its rule is the
     top edge, not the left one (V2.72: missed in the first pass) */
  border-top-color:var(--edge-soft);
}
/* V2.73 (Josh and Zach, 2026-09-23): with the light gone the band ran on
   from the feature tabs with nothing to mark its start. The name takes the
   frame from the Hades box: a white hairline drawn close around it, left
   open where the kicker breaks through its bottom edge, as the chariot
   breaks through the top of the frame on the packaging. The kicker stands on
   the stage's own obsidian, so the gap is exactly as wide as it is. */
:host([data-mode="inline"]) .masthead{ --frame:rgba(243,241,237,.9) }
:host([data-mode="inline"]) .wordmark{
  width:fit-content; max-width:100%;
  margin:0 auto;
  /* the caps' ink sits .15em under the top of the line box and .06em over
     its foot, so this clears it by .45em above and below */
  padding:.3em .62em .39em;
  border:1px solid var(--frame);
}
:host([data-mode="inline"]) .kicker{
  position:relative;
  width:fit-content;
  margin:-.64em auto 0;        /* centred on the frame's bottom line */
  padding:0 1.15em;
  line-height:1.2;
  background:var(--color-obsidian,#111112);
}
/* fit-content stretches a wrapped name's frame to the whole column. On a
   phone WALL MOUNT stacks, so the frame fits its longer word instead. */
@media (max-width:480px){
  :host([data-mode="inline"]) .wordmark{ width:min-content }
}
/* V3.0: on a phone an opened point scrolls the exhibit to the top of the
   screen, and on a product page the fixed header (60px below 990px) is up
   there: the Back control landed under it, behind the menu button. The
   museum has no header, so this is for the inline mount only. */
@media (max-width:880px){
  :host([data-mode="inline"]) .exhibit{ scroll-margin-top:72px }
  /* V3.0 (Josh, 2026-09-29): a 44px touch target around the 34px pin. The
     transparent border widens the hit box; the rings are drawn by the
     pseudo-elements from the padding box, so they do not move. */
  :host([data-mode="inline"]) .point{
    box-sizing:content-box; width:34px; height:34px; padding:0;
    border:5px solid transparent; background-clip:padding-box;
    margin:-22px 0 0 -22px;
  }
}
@media (min-width:881px){
  /* load() writes --stage-h as an inline style on .exhibit, so the inline
     budget has to out-rank it */
  :host([data-mode="inline"]) .exhibit{ --stage-h:clamp(340px,50vh,480px) !important }
  /* the panel is as tall as the masthead plus the stage, so the stage budget
     above is also what keeps Previous/Next inside the band rather than under
     the index rail */
  :host([data-mode="inline"]) .detail{ padding-block:clamp(16px,2vh,26px) }
  :host([data-mode="inline"]) .masthead{ padding-top:clamp(26px,4vh,48px) }
  :host([data-mode="inline"]) .switch{ padding-top:clamp(24px,3vh,36px) }
  :host([data-mode="inline"]) .rail{ padding-bottom:clamp(26px,3.6vh,40px) }
}
/* no gallery to hand back to */
:host([data-leave="none"]) .leave{ display:none }
/* one product: the switcher has nothing to switch */
:host([data-switch="off"]) .switch{ display:none }
:host([data-mode="inline"]) .switch button[hidden]{ display:none }
`;

  const EXHIBIT_HTML = "<section class=\"exhibit\" id=\"exhibit\">\n  <button class=\"back\" id=\"back\"><span class=\"arrow\" aria-hidden=\"true\">&#8592;</span>Back</button>\n  <button class=\"leave\" id=\"leave\"><span class=\"arrow\" aria-hidden=\"true\">&#8592;</span><span class=\"word\">Return to the gallery</span></button>\n\n  <nav class=\"switch\" id=\"switch\" aria-label=\"Choose a product\">\n    <button data-k=\"theseus\" aria-current=\"true\">Theseus</button>\n    <button data-k=\"hades\">Hades</button>\n    <button data-k=\"wall\">Wall Mount</button>\n    <span class=\"ink\" id=\"ink\"></span>\n  </nav>\n  <div class=\"upper\">\n  <div class=\"masthead\">\n    <h2 class=\"wordmark\" id=\"wordmark\"></h2>\n    <p class=\"kicker\" id=\"kicker\"></p>\n  </div>\n\n  <div class=\"theatre\">\n    <div class=\"stage\" id=\"stage\">\n    <div class=\"rig\">\n      <div class=\"hover\">\n        <div class=\"body\" id=\"body\">\n          <img alt=\"\" draggable=\"false\">\n          <!-- hotspots injected -->\n        </div>\n        <div class=\"cast\"></div>\n      </div>\n    </div>\n    </div>\n\n    <aside class=\"detail\" id=\"detail\" role=\"region\" aria-label=\"Build detail\" aria-hidden=\"true\">\n      <button class=\"shut\" id=\"shut\" aria-label=\"Close detail\">&#10005;</button>\n      <div id=\"slot\"></div>\n      <div class=\"step\">\n        <button id=\"prev\">Previous</button>\n        <span class=\"tally\" id=\"tally\"></span>\n        <button id=\"next\">Next</button>\n      </div>\n    </aside>\n  </div>\n  </div>\n\n  <nav class=\"rail\" id=\"rail\" aria-label=\"Build points\"></nav>\n</section>";

  // The section shipped with an assets/ folder beside it. There is no folder
  // here, so every photograph is inlined and the original paths are kept as
  // the keys — which means the product data below reads exactly as it does
  // in the source file, and re-pointing it at hosted URLs stays a one-line
  // change.
  const EXHIBIT_IMG = {"assets/hades-handle.webp": MUSEUM_ASSET('exhibit-hades-handle.webp'), "assets/hades-nameplate.webp": MUSEUM_ASSET('exhibit-hades-nameplate.webp'), "assets/hades-system.webp": MUSEUM_ASSET('exhibit-hades-system.webp'), "assets/hades-telescope.webp": MUSEUM_ASSET('exhibit-hades-telescope.webp'), "assets/hades-wheels.webp": MUSEUM_ASSET('exhibit-hades-wheels.webp'), "assets/hades.webp": MUSEUM_ASSET('exhibit-hades.webp'), "assets/theseus-handle.webp": MUSEUM_ASSET('exhibit-theseus-handle.webp'), "assets/theseus-latch.webp": MUSEUM_ASSET('exhibit-theseus-latch.webp'), "assets/theseus-nameplate.webp": MUSEUM_ASSET('exhibit-theseus-nameplate.webp'), "assets/theseus-plates.webp": MUSEUM_ASSET('exhibit-theseus-plates.webp'), "assets/theseus-system.webp": MUSEUM_ASSET('exhibit-theseus-system.webp'), "assets/theseus.webp": MUSEUM_ASSET('exhibit-theseus.webp'), "assets/wallmount-installed.webp": MUSEUM_ASSET('exhibit-wallmount-installed.webp'), "assets/wallmount-lock.webp": MUSEUM_ASSET('exhibit-wallmount-lock.webp'), "assets/wallmount-rail.webp": MUSEUM_ASSET('exhibit-wallmount-rail.webp'), "assets/wallmount.webp": MUSEUM_ASSET('exhibit-wallmount.webp'),
    /* V3.0: the photo pass */
    "assets/theseus-hinge.webp": MUSEUM_ASSET('exhibit-theseus-hinge.webp'),
    "assets/theseus-foam.webp": MUSEUM_ASSET('exhibit-theseus-foam.webp'),
    "assets/theseus-rubber.webp": MUSEUM_ASSET('exhibit-theseus-rubber.webp'),
    "assets/theseus-handle-v3.webp": MUSEUM_ASSET('exhibit-theseus-handle-v3.webp'),
    "assets/theseus-latch-v3.webp": MUSEUM_ASSET('exhibit-theseus-latch-v3.webp'),
    "assets/hades-hinge.webp": MUSEUM_ASSET('exhibit-hades-hinge.webp'),
    "assets/hades-latch.webp": MUSEUM_ASSET('exhibit-hades-latch.webp'),
    "assets/hades-molle.webp": MUSEUM_ASSET('exhibit-hades-molle.webp'),
    "assets/hades-handles.webp": MUSEUM_ASSET('exhibit-hades-handles.webp'),
    "assets/hades-rubber.webp": MUSEUM_ASSET('exhibit-hades-rubber.webp'),
    "assets/hades-system-v3.webp": MUSEUM_ASSET('exhibit-hades-system-v3.webp'),
    "assets/hades-wheels-v3.webp": MUSEUM_ASSET('exhibit-hades-wheels-v3.webp'),
    "assets/wallmount-trigger.webp": MUSEUM_ASSET('exhibit-wallmount-trigger.webp'),
    "assets/wallmount-installed-v3.webp": MUSEUM_ASSET('exhibit-wallmount-installed-v3.webp')};

  // ---------------------------------------------------------------------
  // The build-detail exhibit, lifted from the FORUM product page and run
  // inside a shadow root. Its CSS was written for a page of its own and
  // names things .stage, .rail, .body, .detail — all of which the museum
  // also uses. Rather than rename thirty-six classes and hope, the whole
  // section gets its own tree: nothing it declares escapes, and nothing
  // the museum declares reaches in. The only things that cross the
  // boundary are the webfonts, which are global by design.
  // ---------------------------------------------------------------------
  function initExhibit(R, onLeave, opts) {

  /* Everything that differs between the three products. Layout values are
     fed straight into CSS custom properties, so a swap is a data change
     rather than a second copy of the section. */
  const PRODUCTS = EXHIBIT_PRODUCTS = {

    theseus:{
      eyebrow:"Rifle case", name:"Theseus",
      alt:"The Theseus rifle case, a ribbed black hard case shown from a raised three-quarter angle.",
      render:"assets/theseus.webp", ar:1579/623,
      caseW:"min(792px,58vw)",
      stageH:"clamp(420px,63vh,600px)",
      stageSm:"max(clamp(210px,27vh,290px), 44vw)",
      railFs:"10.5px", railLs:".06em", railGap:"clamp(8px,1.35vw,20px)",
      railMx:"clamp(20px,3.4vw,54px)", cols:2,
      /* V3.0 (Josh, 2026-09-29): Cody's photographs. hinge, foam and rubber
         are new; handle and latch replace the V2.71 photos. Built-In Handles
         has no photograph yet and shows the close-up of the render. */
      photos:{
  latch : "assets/theseus-latch-v3.webp",
    handle: "assets/theseus-handle-v3.webp",
    system: "assets/theseus-system.webp",
    hinge : "assets/theseus-hinge.webp",
    foam  : "assets/theseus-foam.webp",
    rubber: "assets/theseus-rubber.webp",
    plate : "assets/theseus-nameplate.webp"
      },
      points:[
  {
      id:"system", x:"94.7%", y:"79%",
      name:"Modular System",
      lede:"In seconds you can connect Hades with Theseus for easy mobility—no latches or straps required."
    },
    {
      id:"hinge", x:"31.5%", y:"3.5%",
      name:"316 Stainless Steel Hinges", short:"316 Stainless Hinges",
      lede:"They’re hinges and they work super well. They’re also nearly indestructible."
    },
    {
      id:"plate", x:"70%", y:"34%",
      name:"Metal Name Plate",
      lede:"Because it looks tits."
    },
    {
      id:"handle", x:"31%", y:"39.5%",
      name:"Custom Main Handle",
      lede:"Designed after a charging handle—this one is for the boys."
    },
    {
      id:"handles", x:"93%", y:"54%",
      name:"Built-In Handles",
      lede:"No excess plastic included. Sleek and only what is necessary for easy transportation."
    },
    {
      id:"latch", x:"9.5%", y:"27%",
      name:"Built-In Latch Locks",
      lede:"No padlock purchase required, because it’s 2026 and it doesn’t make sense."
    },
    {
      id:"foam", x:"52%", y:"45%",
      name:"Modular Layered Foam",
      lede:"Layered foam with a durable hook cover protects the foam while allowing your equipment to settle securely into place. Multiple layers give you the flexibility to configure your case around your equipment—without cutting, plucking, or rebuilding the interior every time your setup changes."
    },
    {
      id:"rubber", x:"46%", y:"14%",
      name:"Rubberized Exterior",
      lede:"Reduces slipping or sliding during transportation. Looks and feels fucking amazing to boot."
    }
      ]
    },

    hades:{
      eyebrow:"Range case", name:"Hades",
      alt:"The Hades range case, a ribbed black hard case with wheels and a telescoping handle, shown from a raised three-quarter angle.",
      render:"assets/hades.webp", ar:1542/833,
      caseW:"min(634px,46vw)",
      stageH:"clamp(420px,63vh,600px)",
      stageSm:"max(clamp(210px,27vh,300px), 56vw)",
      railFs:"10px", railLs:".045em", railGap:"clamp(8px,1.1vw,16px)",
      railMx:"clamp(16px,2.2vw,34px)", cols:2,
      /* V3.0 (Josh, 2026-09-29): hinge, latch, molle, handles and rubber are
         new; system and wheels replace the V2.71 photos. hinge, latch and
         rubber are from Zach's folder, the rest are Cody's. Rubberized Feet
         has no photograph yet and shows the close-up of the render. */
      photos:{
  system   : "assets/hades-system-v3.webp",
    handle   : "assets/hades-handle.webp",
    hinge    : "assets/hades-hinge.webp",
    rubber   : "assets/hades-rubber.webp",
    plate    : "assets/hades-nameplate.webp",
    molle    : "assets/hades-molle.webp",
    latch    : "assets/hades-latch.webp",
    handles  : "assets/hades-handles.webp",
    wheels   : "assets/hades-wheels-v3.webp",
    telescope: "assets/hades-telescope.webp"
      },
      points:[
  {
      id:"system", x:"23%", y:"6.5%",
      name:"Modular System",
      lede:"Connect Hades with Theseus in seconds for easy mobility—no latches or straps required."
    },
    {
      id:"hinge", x:"41%", y:"3%",
      name:"316 Stainless Steel Hinges", short:"316 Stainless Hinges",
      lede:"Nearly indestructible hinges that aren’t hideous plastic."
    },
    {
      id:"rubber", x:"18.8%", y:"46.4%",
      name:"Rubberized Exterior",
      lede:"Reduces slipping or sliding during transportation. Looks and feels fucking amazing to boot."
    },
    {
      id:"plate", x:"63%", y:"15%",
      name:"Metal Name Plate",
      short:"Name Plate",
      lede:"’Nuff said."
    },
    {
      id:"feet", x:"95%", y:"65.5%",
      name:"Rubberized Feet",
      lede:"Protects the hinges."
    },
    {
      id:"molle", x:"49%", y:"14.3%",
      name:"Built-In Velcro MOLLE Panel", short:"Velcro MOLLE Panel",
      lede:"Secured inside the lid is a large Velcro MOLLE panel that is easy to remove and customize for all of your smaller items or pouches. No Velcro strips required."
    },
    {
      id:"handle", x:"33%", y:"43%",
      name:"Custom Main Handle",
      lede:"Designed after a charging handle—this one is for the boys."
    },
    {
      id:"latch", x:"61.5%", y:"49%",
      name:"Built-In Latch Locks",
      lede:"No padlock purchase required, because it’s 2026 and it’s about time someone did it."
    },
    {
      id:"handles", x:"89.5%", y:"38.5%",
      name:"Built-In Handles",
      lede:"No excess plastic included. Sleek and only what is necessary for easy transportation."
    },
    {
      id:"wheels", x:"4.5%", y:"50%",
      name:"Skateboard Wheels", short:"Wheels",
      lede:"Custom polyurethane skateboard wheels. They run quiet over concrete and gravel and take a curb without chatter, so a loaded case moves easily and without announcing itself."
    },
    {
      id:"telescope", x:"86%", y:"77%",
      name:"Telescoping Handle",
      lede:"The telescoping handle was built from the ground up. It carries the column aesthetic through from the shell, and the rubberized grip gives a solid hold and full control with the case under load."
    }
      ]
    },

    wall:{
      eyebrow:"For Theseus", name:"Wall Mount",
      alt:"The FORUM Wall Mount, a formed steel shelf shown from a raised three-quarter angle.",
      render:"assets/wallmount.webp", ar:1574/479,
      caseW:"min(720px,50vw)",
      stageH:"clamp(360px,52vh,510px)",
      stageSm:"max(clamp(190px,24vh,260px), 34vw)",
      railFs:"11.5px", railLs:".08em", railGap:"clamp(10px,1.6vw,24px)",
      railMx:"clamp(18px,3vw,46px)", cols:1,
      /* V3.0 (Josh, 2026-09-29): trigger is new, from Zach's folder; fit
         takes Cody's photograph of Theseus on the mount. */
      photos:{
    steel  : "assets/wallmount-rail.webp",
    lock   : "assets/wallmount-lock.webp",
    fit    : "assets/wallmount-installed-v3.webp",
    trigger: "assets/wallmount-trigger.webp"
      },
      points:[
  {
      id:"steel", x:"60%", y:"15%",
      name:"Formed Steel",
      lede:"Formed bent steel with a custom front-mounted lock. Anchors directly into wall studs."
    },
    {
      id:"lock", x:"32%", y:"60%",
      name:"Front-Mounted Lock",
      lede:"The front-mounted lock secures Theseus in place. The case stays on display, and stays yours."
    },
    {
      id:"fit", x:"72%", y:"48%",
      name:"Built For Theseus",
      lede:"Designed exclusively for Theseus. Every contact point is purpose-built for its shell. Nothing else fits, by design."
    },
    {
      id:"trigger", x:"43.1%", y:"69.9%",
      name:"Trigger Release",
      lede:"Lift and pull to retract two locking pins, allowing the Wall Mount and Theseus to pivot 90° and sit flush against the wall—freeing up valuable space when the case is not in use."
    }
      ]
    }
  };
  let POINTS = [], PHOTOS = {};

  /* Everything that differs about an inline mount is read off opts, which
     the museum does not pass. With no opts every branch guarded below is
     skipped and the exhibit behaves exactly as it did.
       mode:'inline'  page furniture rather than a full-screen takeover
       product:'hades'  the product to open on
       products:[...]   the products the switcher offers; one hides it */
  const OPTS   = opts || {};
  const inline = OPTS.mode === 'inline';
  const OFFER  = Array.isArray(OPTS.products)
    ? OPTS.products.filter(k => PRODUCTS[k])
    : null;
  const START  = (OPTS.product && PRODUCTS[OPTS.product])
    ? OPTS.product
    : ((OFFER && OFFER[0]) || 'theseus');


  const exhibit = R.getElementById('exhibit');
  const bodyEl  = R.getElementById('body');
  const detail  = R.getElementById('detail');
  const slot    = R.getElementById('slot');
  const rail    = R.getElementById('rail');
  const tally   = R.getElementById('tally');
  const stage   = R.getElementById('stage');
  const render  = bodyEl.querySelector('img');


  /* Frame a close crop of the render on a given point.
     Background is 300% of the panel width; solve for the background-position
     that lands the feature in the middle of the 2.4:1 window, then clamp so
     points near the ends of the case stay inside the frame. */
  const ZOOM = 3, BOX_AR = 4/3, KX = ZOOM;
  let KY;
  const frame = (f,k) => Math.max(0, Math.min(100, (k*parseFloat(f) - 50) / (k-1)));
  let active = -1, current = null;

  /* build hotspots + index rail for the current product */
  let dots = [], tabs = [], first = true;

  function build(){
    bodyEl.querySelectorAll('.point').forEach(n=> n.remove());
    rail.textContent = '';

    POINTS.forEach((p,i)=>{
      const b = document.createElement('button');
      b.className = 'point';
      b.style.setProperty('--x',p.x);
      b.style.setProperty('--y',p.y);
      b.style.setProperty('--in', (first ? 0.75 + i*0.11 : 0.12 + i*0.06)+'s');
      b.style.setProperty('--ping',(1.6 + i*0.3)+'s');
      b.setAttribute('aria-expanded','false');
      b.setAttribute('aria-controls','detail');
      b.setAttribute('aria-label',p.name);
      const px = parseFloat(p.x);
      if(px < 20) b.classList.add('edge-l');
      else if(px > 75) b.classList.add('edge-r');
      if(parseFloat(p.y) < 42) b.classList.add('edge-t');
      b.innerHTML = '<span class="ping"></span><span class="tip" aria-hidden="true">' + p.name + '</span>';
      b.dataset.i = i;
      b.addEventListener('click',()=> i===active ? close() : open(i));
      bodyEl.appendChild(b);

      const r = document.createElement('button');
      r.textContent = p.short || p.name;
      r.setAttribute('aria-current','false');
      r.dataset.i = i;
      r.addEventListener('click',()=> i===active ? close() : open(i));
      rail.appendChild(r);
    });

    dots = [...bodyEl.querySelectorAll('.point')];
    tabs = [...rail.querySelectorAll('button')];

    /* the phone index is a grid, so the final row carries no rule under it */
    const cols = PRODUCTS[current].cols;
    const lastRow = tabs.length - (tabs.length % cols || cols);
    tabs.forEach((t,i)=>{ if(i >= lastRow) t.classList.add('last-row'); });

    /* Hovering a rail label raises that marker's name on the case, and
       hovering a marker lights its label. Pointer events are gated to
       hover-capable input so a tap doesn't leave a marker stuck on. */
    [tabs, dots].forEach(list=>{
      list.forEach((el,i)=>{
        if(hoverable){
          el.addEventListener('pointerenter',()=> cue(i,true));
          el.addEventListener('pointerleave',()=> cue(i,false));
        }
        el.addEventListener('focus',()=> cue(i,true));
        el.addEventListener('blur', ()=> cue(i,false));
      });
    });
    first = false;
  }

  function cue(i,on){
    dots[i].classList.toggle('is-cued', on);
    tabs[i].classList.toggle('is-cued', on);
  }
  const hoverable = matchMedia('(hover: hover) and (pointer: fine)').matches;

  function open(i){
    active = i;
    const p = POINTS[i];

    slot.innerHTML =
      (PHOTOS[p.id]
        ? '<div class="shot photo'+(p.todo?' todo':'')+'" role="img" aria-label="'+
            (p.todo?'Placeholder photograph of the ':'Photograph of the ')+p.name.toLowerCase()+'" '+
            'style="background-image:url(&quot;'+PHOTOS[p.id]+'&quot;)"></div>'
        /* V3.0 (Josh): a point with no photograph shows the close-up of the
           render, plainly. The pink "Need image" label is only for a point
           flagged todo:true, and none is. */
        : '<div class="shot'+(p.todo?' todo':'')+'" role="img" aria-label="'+
            (p.todo?'Placeholder: awaiting photography of the ':'Close view of the ')+p.name.toLowerCase()+'" '+
            'style="background-image:url(&quot;'+render.src+'&quot;);'+
            '--bgx:'+frame(p.x,KX).toFixed(2)+'%;--bgy:'+frame(p.y,KY).toFixed(2)+'%"></div>')+
      '<h3 tabindex="-1" id="detail-title">'+p.name+'</h3>'+
      '<p class="lede">'+p.lede+'</p>';
    slot.classList.remove('fade'); void slot.offsetWidth; slot.classList.add('fade');

    dots.forEach((d,n)=> d.setAttribute('aria-expanded', n===i ? 'true':'false'));
    tabs.forEach((t,n)=> t.setAttribute('aria-current', n===i ? 'true':'false'));

    tally.textContent = (i+1)+' / '+POINTS.length;
    detail.setAttribute('aria-hidden','false');
    exhibit.classList.add('is-open');
    R.getElementById('detail-title').focus({preventScroll:true});

    // on narrow screens the panel opens below the case — bring it into view
    if(window.matchMedia('(max-width:880px)').matches){
      setTimeout(()=> exhibit.scrollIntoView({block:'start', behavior:'smooth'}), 160);
    }
  }

  function close(){
    const last = active;
    active = -1;
    exhibit.classList.remove('is-open');
    detail.setAttribute('aria-hidden','true');
    dots.forEach(d=> d.setAttribute('aria-expanded','false'));
    tabs.forEach(t=> t.setAttribute('aria-current','false'));
    /* On a phone the back control disappears with the panel, and returning
       focus to the marker would leave its label hanging over the render,
       so drop focus instead. On desktop, hand it back to the marker. */
    if(last > -1){
      if(window.matchMedia('(max-width:880px)').matches){
        const ae = R.activeElement || document.activeElement;
        if(ae) ae.blur();
      }else{
        dots[last].focus({preventScroll:true});
      }
    }
  }

  const wrap = n => (n + POINTS.length) % POINTS.length;
  R.getElementById('next').addEventListener('click',()=> open(wrap(active+1)));
  R.getElementById('prev').addEventListener('click',()=> open(wrap(active-1)));
  /* Swap products by rewriting the data and the layout variables.
     Nothing is duplicated in the DOM — one section serves all three. */
  function load(key){
    const P = PRODUCTS[key];
    current = key;
    POINTS = P.points;
    PHOTOS = {};
    for (const k in P.photos) PHOTOS[k] = EXHIBIT_IMG[P.photos[k]] || P.photos[k];
    KY = ZOOM * BOX_AR / P.ar;

    render.src = EXHIBIT_IMG[P.render] || P.render;
    render.alt = P.alt;
    R.getElementById('kicker').textContent = P.eyebrow;
    R.getElementById('wordmark').textContent = P.name;

    const st = exhibit.style;
    st.setProperty('--case-w',    P.caseW);
    st.setProperty('--stage-h',   P.stageH);
    st.setProperty('--stage-h-sm',P.stageSm);
    st.setProperty('--rail-fs',   P.railFs);
    st.setProperty('--rail-ls',   P.railLs);
    st.setProperty('--rail-gap',  P.railGap);
    st.setProperty('--rail-mx',   P.railMx);
    st.setProperty('--rail-cols', P.cols);
    rail.className = 'rail cols-' + P.cols;

    build();
    chips.forEach(c=> c.setAttribute('aria-current', c.dataset.k===key ? 'true':'false'));
    moveInk();
  }

  const chips = [...R.querySelectorAll('.switch button')];
  const ink   = R.getElementById('ink');
  const swtch = R.getElementById('switch');

  function moveInk(){
    const c = chips.find(c=> c.getAttribute('aria-current')==='true');
    if(!c) return;
    const a = c.getBoundingClientRect(), b = swtch.getBoundingClientRect();
    ink.style.left  = (a.left - b.left) + 'px';
    ink.style.width = a.width + 'px';
  }

  const ORDER = Object.keys(PRODUCTS);

  function swap(key){
    if(key === current) return;
    const dir = ORDER.indexOf(key) > ORDER.indexOf(current) ? 1 : -1;
    if(active > -1) close();

    /* mark the choice straight away so the rule starts moving on the click,
       not when the new product finishes loading */
    chips.forEach(c=> c.setAttribute('aria-current', c.dataset.k===key ? 'true':'false'));
    moveInk();

    exhibit.style.setProperty('--exit', (dir>0 ? -34 : 34)+'px');
    exhibit.classList.add('swapping');

    setTimeout(()=>{
      /* park the incoming product on the far side with no transition,
         then release it so it travels back to centre */
      exhibit.classList.add('no-anim');
      load(key);
      exhibit.style.setProperty('--exit', (dir>0 ? 34 : -34)+'px');
      void exhibit.offsetWidth;
      exhibit.classList.remove('no-anim');
      requestAnimationFrame(()=> exhibit.classList.remove('swapping'));
    }, 300);
  }

  chips.forEach(c=> c.addEventListener('click',()=> swap(c.dataset.k)));
  window.addEventListener('resize', moveInk);
  document.fonts.ready.then(moveInk);


  R.getElementById('shut').addEventListener('click', close);
  R.getElementById('back').addEventListener('click', close);
  R.getElementById('leave').addEventListener('click', onLeave);

  function onKey(e){
    if(e.key === 'Escape'){
      // One Escape shuts an open build point; a second one leaves the exhibit
      // and hands the visitor back to the gallery.
      // Inline there is no gallery to go back to, so Escape only ever shuts
      // an open build point.
      if(active > -1) close(); else if(!inline) onLeave();
      e.stopPropagation();
      return;
    }
    if(active < 0) return;
    if(e.key === 'ArrowRight'){ e.preventDefault(); open(wrap(active+1)); }
    if(e.key === 'ArrowLeft'){  e.preventDefault(); open(wrap(active-1)); }
  }

  /* pointer parallax — the case turns a few degrees toward the cursor */
  const calm = matchMedia('(prefers-reduced-motion: reduce)');
  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  let raf = 0, tx = 0, ty = 0, cx = 0, cy = 0;

  function loop(){
    cx += (tx - cx) * 0.07;
    cy += (ty - cy) * 0.07;
    bodyEl.style.transform = 'rotateX('+cy.toFixed(3)+'deg) rotateY('+cx.toFixed(3)+'deg)';
    raf = (Math.abs(tx-cx) > .01 || Math.abs(ty-cy) > .01) ? requestAnimationFrame(loop) : 0;
  }
  function kick(){ if(!raf) raf = requestAnimationFrame(loop); }

  if(!calm.matches && fine.matches){
    stage.addEventListener('pointermove', e=>{
      const r = stage.getBoundingClientRect();
      tx =  ((e.clientX - r.left) / r.width  - .5) * 5.2;
      ty = -((e.clientY - r.top ) / r.height - .5) * 3.4;
      kick();
    });
    stage.addEventListener('pointerleave',()=>{ tx = 0; ty = 0; kick(); });
  }

  /* Inline the exhibit is one section among several and cannot own the
     document's keys, so the handler hangs off its own root instead: a
     keydown only reaches it while focus is inside the exhibit, and an arrow
     pressed anywhere else on the page still scrolls the page. The museum
     keeps feeding onKey itself through api.key(). */
  if(inline){
    exhibit.addEventListener('keydown', onKey);
    if(OFFER){
      chips.forEach(c=> { if(!OFFER.includes(c.dataset.k)) c.hidden = true; });
      if(OFFER.length < 2) R.host.setAttribute('data-switch','off');
    }
    /* the museum calls api.show() once it has raised the overlay; an inline
       exhibit is up the moment it is built */
    load(START);
  }

    return {
      show(key){ if(key !== current) { if(active > -1) close(); load(key); } else moveInk(); },
      shut(){ if(active > -1) close(); },
      isDetailOpen(){ return active > -1; },
      key(e){ onKey(e); },
      focus(){ R.getElementById('leave').focus({ preventScroll:true }); }
    };
  }

  // buildExhibit(), lifted from the museum with two changes: it takes the way
  // out and the container as options, and it returns the host and the API
  // rather than writing them into museum-scope variables.
  window.FORUM_EXHIBIT = {
    mount(opts) {
      const onLeave = (opts && opts.onLeave) || function () {};
      const inline = !!(opts && opts.mode === 'inline');
      const host = document.createElement('div');
      /* The museum's single overlay is positioned through #caseExhibit in
         the page's own CSS and stays aria-hidden until it is raised. An
         inline host is page furniture — possibly more than one per page —
         so it takes a class instead of that shared id, is never hidden from
         the accessibility tree, and publishes its mode (and its missing
         exit) for the rules at the foot of the stylesheet. */
      if (inline) {
        host.className = 'forum-exhibit-host';
        host.setAttribute('data-mode', 'inline');
        if (!(opts && opts.onLeave)) host.setAttribute('data-leave', 'none');
      } else {
        host.id = 'caseExhibit';
        host.setAttribute('aria-hidden', 'true');
      }
      const root = host.attachShadow({ mode: 'open' });
      const sheet = document.createElement('style');
      sheet.textContent = EXHIBIT_CSS + INLINE_CSS;
      root.appendChild(sheet);
      const holder = document.createElement('div');
      holder.innerHTML = EXHIBIT_HTML;
      while (holder.firstChild) root.appendChild(holder.firstChild);
      ((opts && opts.container) || document.body).appendChild(host);
      return { host: host, api: initExhibit(root, onLeave, opts) };
    },
    close(mounted) {
      if (!mounted) return;
      if (mounted.api) mounted.api.shut();
      if (mounted.host) {
        mounted.host.classList.remove('is-up');
        mounted.host.setAttribute('aria-hidden', 'true');
      }
    },
    image(key) { return EXHIBIT_IMG[key] || key; },
    get images() { return EXHIBIT_IMG; },
    get PRODUCTS() { return EXHIBIT_PRODUCTS; }
  };
})();
